import React from 'react'
import { Flex, Text, Box } from '@chakra-ui/react'
import Base from './base'
import '../styles/privacy.css'
import { Helmet } from 'react-helmet'
import { LinkedInIcon } from '../assets/icons'
// Note: Add founder photo at src/assets/images/founder-photo.jpg
// Supported formats: .jpg, .jpeg, .png
import founderPhoto from '../assets/images/founder-photo.jpg'

const MeetTheFounder = () => {
  return (
    <Base isPublic hideBg={true} bottomBar={false} allowFullHeight={true}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Coteri - Meet the Founder</title>
        <meta
          name="description"
          content="Meet the founder of Coteri. Learn about the story behind creating a platform that makes gathering feel better through collaborative music playlists."
        />
      </Helmet>
      <Flex flex="1" direction="column" mb="280px">
        <div className="privacy">
          {/* Header Section with Photo, Name, and LinkedIn */}
          <Flex
            direction="column"
            alignItems="center"
            mb="40px"
            mt="20px"
          >
            <Box
              as="img"
              src={founderPhoto}
              alt="Jonathan Cooper Orler"
              borderRadius="50%"
              width="220px"
              height="220px"
              objectFit="contain"
              objectPosition="center"
              mb="20px"
              bg="gray.100"
            />
            <Text
              fontSize="28px"
              fontWeight="600"
              color="#000000"
              mb="12px"
            >
              Jonathan Cooper Orler
            </Text>
            <Text
              fontSize="16px"
              color="#666666"
              mb="20px"
            >
              Founder of Coteri
            </Text>
            <a
              href="https://www.linkedin.com/in/jorler/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <LinkedInIcon fill="#0077B5" width={24} height={24} />
            </a>
          </Flex>

          {/* Main Story Section */}
          <p className="c1">
            <span className="c3" style={{ fontWeight: '600', fontSize: '18pt' }}>
              Why I Built Coteri
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>
          <p className="c1">
            <span className="c2">
              Music is one of the last things people think about when planning a party—and one of the first things everyone notices once it starts.
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>
          <p className="c1">
            <span className="c2">
              I've seen playlists get handed off at the last minute to "the music person," often someone who's already tired of managing the vibe—or quietly dealing with aux-anxiety. As friend groups blend, tastes diverge, and the energy of a room can shift fast. What starts as a great playlist can quickly feel off.
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>
          <p className="c1">
            <span className="c2">
              So I built Coteri to change that dynamic.
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>
          <p className="c1">
            <span className="c2">
              Coteri creates playlists that are grounded in the host's taste—setting the initial vibe—and then evolve as guests contribute their preferences. The result isn't chaos or compromise. It's a shared soundtrack that reflects the room as it actually feels, not just one person's library.
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>
          <p className="c1">
            <span className="c2">
              Hosting should feel connective, not performative. Music should bring people together, not put pressure on one person to get it "right."
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>

          {/* Why "Coteri" Section */}
          <Box
            bg="#f6f6fb"
            p="24px"
            borderRadius="8px"
            mb="24px"
            borderLeft="4px solid"
            borderColor="primary"
          >
            <p className="c1">
              <span className="c3" style={{ fontWeight: '600', fontSize: '16pt' }}>
                Why the Name Coteri
              </span>
            </p>
            <p className="c1 c9">
              <span className="c2"></span>
            </p>
            <p className="c1">
              <span className="c2">
                <strong>Coterie</strong>
              </span>
            </p>
            <p className="c1">
              <span className="c2">
                <em>noun</em> — a small group of people with shared interests
              </span>
            </p>
            <p className="c1 c9">
              <span className="c2"></span>
            </p>
            <p className="c1">
              <span className="c2">
                Coteri is a modern take on that idea.
              </span>
            </p>
            <p className="c1 c9">
              <span className="c2"></span>
            </p>
            <p className="c1">
              <span className="c2">
                Every gathering is its own coterie—friends, neighbors, coworkers, chosen family—coming together for a moment. Coteri exists to support those moments, not extract value from them.
              </span>
            </p>
            <p className="c1 c9">
              <span className="c2"></span>
            </p>
            <p className="c1">
              <span className="c2" style={{ fontWeight: '600' }}>
                Grounded in a sense of belonging.
              </span>
            </p>
          </Box>

          {/* How We're Different Section */}
          <p className="c1">
            <span className="c3" style={{ fontWeight: '600', fontSize: '18pt' }}>
              Built for Community, Not Surveillance
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>
          <p className="c1">
            <span className="c2">
              There are great tools for hosting events, and platforms like Partiful have helped modernize invitations.
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>
          <p className="c1">
            <span className="c2">
              Where Coteri is different is intent.
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>
          <p className="c1">
            <span className="c2">
              We don't build profiles to map social graphs.
            </span>
          </p>
          <p className="c1">
            <span className="c2">
              We don't monetize who you spend time with.
            </span>
          </p>
          <p className="c1">
            <span className="c2">
              We don't sell insight into your relationships.
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>
          <p className="c1">
            <span className="c2">
              Coteri exists to make gatherings feel better—not to monitor them. We believe community creates joy, surfaces empathy, and bridges gaps that algorithms often widen. That belief shows up in how we design, what we collect, and what we deliberately don't.
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>

          {/* About Me Section */}
          <p className="c1">
            <span className="c3" style={{ fontWeight: '600', fontSize: '18pt' }}>
              Background
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>
          <p className="c1">
            <span className="c2">
              I studied Industrial Engineering at University of Wisconsin–Madison, where I focused on how complex systems break—and how to redesign them so people don't have to think so hard just to participate.
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>
          <p className="c1">
            <span className="c2">
              Since then, I've worked across startups and large organizations, helping teams simplify workflows, align incentives, and build tools that actually get used. Along the way, I've led projects touching manufacturing, operations, and product systems—including work with Tesla, where scale and coordination aren't optional—they're existential.
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>
          <p className="c1">
            <span className="c2">
              That background heavily informs Coteri. At its core, it's a systems problem: how do you reduce friction, distribute ownership, and still preserve a shared outcome people care about?
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>

          {/* Closing Statement */}
          <p className="c1" style={{ marginTop: '32px' }}>
            <span className="c3" style={{ fontWeight: '600', fontSize: '20pt', lineHeight: '1.4' }}>
              Coteri is my attempt to build technology that respects people, protects community, and makes gathering a little more human.
            </span>
          </p>
          <p className="c1 c9">
            <span className="c2"></span>
          </p>

          {/* Signature */}
          <Flex direction="column" alignItems="flex-start" mt="40px">
            <Text fontSize="16pt" color="#000000" mb="4px">
              — Jonathan Cooper Orler
            </Text>
            <Text fontSize="14pt" color="#666666" fontStyle="italic" mb="4px">
              Founder, Coteri
            </Text>
            <Text fontSize="12pt" color="#666666" opacity={0.75}>
              Since 2021
            </Text>
          </Flex>
        </div>
      </Flex>
    </Base>
  )
}

export default MeetTheFounder

